import mainHTTPClient from '../clients/mainHTTPClient'
import { MAIN_HTTP_API_ENDPOINTS } from '../constants/api/mainHttp'
import { BROWSER_NOTIFICATION_PERMISSION_VALUES } from '../constants/general'
import { resolveThemeName } from '../modules/theming/resolveThemeName'
import { syncBrowserPushWithPreference } from '../modules/push/browserPush'

const BROWSER_NOTIFICATION_SETTING_LOCAL_STORAGE_KEY = 'notificationsTurnedOff'
const THEME_SETTING_LOCAL_STORAGE_KEY = 'theme'

/**
 * Get profile data of logged in user.
 * @returns {Promise<AxiosResponse<object>>}: profile data.
 * @constructor
 */
const GetProfileInfo = async () => {
  const profileInfo = await mainHTTPClient.get(
    MAIN_HTTP_API_ENDPOINTS.PROFILE
  )
  let browserNotificationsTurnedOff = window.localStorage.getItem(BROWSER_NOTIFICATION_SETTING_LOCAL_STORAGE_KEY)
  browserNotificationsTurnedOff &&= JSON.parse(browserNotificationsTurnedOff)
  profileInfo.data.browserNotification = window.Notification.permission === BROWSER_NOTIFICATION_PERMISSION_VALUES.GRANTED && !browserNotificationsTurnedOff

  const storedTheme = window.localStorage.getItem(THEME_SETTING_LOCAL_STORAGE_KEY)
  if (storedTheme != null && storedTheme !== '') {
    try {
      const parsed = JSON.parse(storedTheme)
      profileInfo.data.theme = resolveThemeName(typeof parsed === 'string' ? parsed : storedTheme)
    } catch {
      profileInfo.data.theme = resolveThemeName(storedTheme)
    }
  }

  return profileInfo
}

/**
 * Update logged-in user's profile data.
 * @param profileData {object}
 * @returns {Promise<AxiosResponse<any>>}: updated profile data.
 * @constructor
 */
const Update = async profileData => {
  const updateResult = await mainHTTPClient.put(
    MAIN_HTTP_API_ENDPOINTS.PROFILE,
    profileData
  )

  let notificationsAllowed = profileData.browserNotification
  if (profileData.browserNotification) {
    if (window.Notification.permission !== BROWSER_NOTIFICATION_PERMISSION_VALUES.GRANTED) {
      const notificationRequestResult = await window.Notification.requestPermission()
      notificationsAllowed = notificationRequestResult === BROWSER_NOTIFICATION_PERMISSION_VALUES.GRANTED
    }
  }

  window.localStorage.setItem(
    BROWSER_NOTIFICATION_SETTING_LOCAL_STORAGE_KEY,
    JSON.stringify(!notificationsAllowed)
  )
  window.localStorage.setItem(
    THEME_SETTING_LOCAL_STORAGE_KEY,
    JSON.stringify(profileData.theme)
  )

  try {
    await syncBrowserPushWithPreference(notificationsAllowed)
  } catch (err) {
    console.warn('[ProfileService.Update] browser push sync failed:', err?.message || err)
  }

  return updateResult
}

/**
 * Delete logged-in user's profile.
 * @returns {Promise<AxiosResponse<string>>}: OK in case the deletion was successful.
 * @constructor
 */
const Delete = async () => mainHTTPClient.delete(
  MAIN_HTTP_API_ENDPOINTS.PROFILE
)

/**
 * Set or change the logged-in user's password (separate from profile update).
 * @param password {string}
 * @returns {Promise<AxiosResponse<any>>}
 */
const ChangePassword = async password => mainHTTPClient.put(
  MAIN_HTTP_API_ENDPOINTS.PROFILE_PASSWORD,
  { password }
)

const ProfileService = {
  GetProfileInfo,
  Update,
  ChangePassword,
  Delete
}

export default ProfileService
